import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiFilm, FiDownloadCloud, FiClock } from 'react-icons/fi';
import Pagination from '../reusable/Pagination';


const RecordingTable = ({
    records,
    loader,
    search,
    handleSearchChange,
    currentPage,
    totalPages,
    onPageChange
}) => {
    const tableContainerVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.3, ease: "easeInOut" }
        }
    };

    const rowVariants = {
        hidden: { opacity: 0, x: -50 },
        visible: (i) => ({
            opacity: 1,
            x: 0,
            transition: { delay: i * 0.05, duration: 0.3 }
        }),
        exit: { opacity: 0, x: 50 }
    };

    const tableHeaders = [
        "Course Title",
        "Lesson Title",
        "File Name",
        "Created",
        "Action"
    ];

    return (
        <motion.div
            initial="hidden"
            animate="visible"
            variants={tableContainerVariants}
            className="w-full mx-auto mt-8 px-2 sm:px-4 lg:px-0"
        >
            <div className="bg-white border border-gray-100 shadow-xl rounded-2xl p-4 sm:p-6 lg:p-8 relative">
                {/* Header */}
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <FiFilm className="text-primary w-8 h-8" />
                        <div>
                            <h1 className="font-anek-odia text-2xl sm:text-3xl font-semibold text-gray-900">
                                Session Recordings
                            </h1>
                            <p className="mt-1 text-sm text-gray-500">
                                Explore and access live session recordings.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Header: Search */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.2 }}
                    className="mt-5 flex flex-col sm:flex-row justify-between items-start sm:items-center border-t border-gray-100 pt-4 gap-3"
                >
                    <span className="text-sm sm:text-base font-medium text-gray-800">
                        All recordings
                    </span>
                    <div className="relative w-full sm:w-64">
                        <input
                            type="text"
                            placeholder="Search recordings..."
                            onChange={handleSearchChange}
                            value={search}
                            className="w-full rounded-full border border-gray-200 bg-gray-50 px-10 py-2.5 text-sm text-gray-700 placeholder:text-gray-400 outline-none focus:border-primary focus:bg-white focus:ring-1 focus:ring-primary transition"
                        />
                        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-4 w-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M21 21l-4.35-4.35M11 5a6 6 0 100 12 6 6 0 000-12z"
                                />
                            </svg>
                        </span>
                    </div>
                </motion.div>

                {loader ? (
                    <motion.div
                        initial={{ rotate: 0 }}
                        animate={{ rotate: 360 }}
                        transition={{ repeat: Infinity, duration: 1 }}
                        className="w-8 h-8 border-4 border-blue-200 border-t-blue-500 rounded-full mx-auto my-8"
                    />
                ) : (
                    <div className="mt-6">
                        <div className="rounded-2xl border border-gray-100 bg-white overflow-hidden">
                            <div className="w-full overflow-x-auto">
                                <div className="min-w-[960px]">
                                    <table className="w-full table-auto">
                                        <thead className="bg-gray-50 sticky top-0 z-10">
                                            <tr>
                                                {tableHeaders.map((header, idx) => (
                                                    <motion.th
                                                        key={idx}
                                                        initial={{ opacity: 0 }}
                                                        animate={{ opacity: 1 }}
                                                        transition={{ delay: idx * 0.1 }}
                                                        className="px-4 sm:px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 border-b border-gray-100"
                                                    >
                                                        {header}
                                                    </motion.th>
                                                ))}
                                            </tr>
                                        </thead>

                                        <tbody className="divide-y divide-gray-100">
                                            <AnimatePresence>
                                                {records.map((record, index) => (
                                                    <motion.tr
                                                        key={record.id}
                                                        custom={index}
                                                        initial="hidden"
                                                        animate="visible"
                                                        exit="exit"
                                                        variants={rowVariants}
                                                        whileHover={{ scale: 1.02 }}
                                                        className="hover:bg-gray-50 transition-colors cursor-auto text-sm"
                                                    >
                                                        <td className="px-4 sm:px-6 py-4 font-medium text-gray-900">
                                                            {record.course_details.course_title}
                                                        </td>
                                                        <td className="px-4 sm:px-6 py-4 font-medium text-gray-700">
                                                            {record.course_details.lesson_title}
                                                        </td>
                                                        <td className="px-4 sm:px-6 py-4">
                                                            <div className="flex items-center gap-3">
                                                                <div className="p-2 bg-blue-50 rounded-lg">
                                                                    <FiFilm className="text-blue-500 text-xl" />
                                                                </div>
                                                                <span className="truncate text-gray-700">
                                                                    {record.filename}
                                                                </span>
                                                            </div>
                                                        </td>
                                                        <td className="px-4 sm:px-6 py-4">
                                                            <div className="flex items-center gap-2 text-gray-500">
                                                                <FiClock />
                                                                {record.created_at}
                                                            </div>
                                                        </td>
                                                        <td className="px-4 sm:px-6 py-4">
                                                            <motion.a
                                                                href={record.recording_url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
                                                                whileHover={{ scale: 1.05 }}
                                                                whileTap={{ scale: 0.95 }}
                                                            >
                                                                <FiDownloadCloud />
                                                                View
                                                            </motion.a>
                                                        </td>
                                                    </motion.tr>
                                                ))}
                                            </AnimatePresence>
                                        </tbody>
                                    </table>

                                    {records.length === 0 && !loader && (
                                        <motion.div
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            className="text-center py-8 text-gray-500"
                                        >
                                            No recordings available
                                        </motion.div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Pagination */}
                        {records.length > 0 && (
                            <div className="flex justify-between items-center mt-4">
                                <p className="hidden sm:block text-xs text-gray-500">
                                    Page{" "}
                                    <span className="font-semibold">{currentPage}</span> of{" "}
                                    <span className="font-semibold">{totalPages}</span>
                                </p>
                                <Pagination
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    onPageChange={onPageChange}
                                />
                            </div>
                        )}
                    </div>
                )}
            </div>
        </motion.div>
    );
};

export default RecordingTable;